import { useEffect } from 'react';
import { create } from 'zustand';
import type { QualityContext, QualityItemIn } from '../api/types';

/** Route name of a path — what «додати цей екран» reports as `screen`. */
export function screenName(pathname: string): string {
  const p = pathname.replace(/\/+$/, '') || '/';
  const rules: [RegExp, string][] = [
    [/^\/$/, 'home'],
    [/^\/handovers\/[^/]+$/, 'handover'],
    [/^\/handovers$/, 'handovers'],
    [/^\/cases\/new$/, 'case_new'],
    [/^\/cases\/[^/]+$/, 'case'],
    [/^\/cases$/, 'cases'],
    [/^\/trips$/, 'trips'],
    [/^\/vehicles$/, 'vehicles'],
    [/^\/more$/, 'settings'],
    [/^\/password$/, 'password'],
    [/^\/login$/, 'login'],
  ];
  for (const [re, name] of rules) if (re.test(p)) return name;
  return 'other';
}

export interface ScreenExtra {
  handover_id?: string;
  case_number?: string;
}

/** Pages announce what they show (the handover id, the number of an own case); the problem button reads it. */
export const useScreenExtra = create<{ extra: ScreenExtra; path: string | null }>(() => ({ extra: {}, path: null }));

export function useAnnounceScreen(path: string, extra: ScreenExtra) {
  const { handover_id, case_number } = extra;
  useEffect(() => {
    useScreenExtra.setState({ path, extra: { handover_id, case_number } });
    return () => {
      if (useScreenExtra.getState().path === path) useScreenExtra.setState({ path: null, extra: {} });
    };
  }, [path, handover_id, case_number]);
}

export interface ContextInput {
  pathname: string;
  url: string;
  appVersion: string;
  locale: string;
  userAgent: string;
  extra?: ScreenExtra;
}

/** The `context` of POST /partner/quality-items. Only ids the partner already sees — no personal data. */
export function buildContext(i: ContextInput): QualityContext {
  const ctx: QualityContext = {
    screen: screenName(i.pathname),
    url: i.url.slice(0, 500),
    app_version: i.appVersion,
    locale: i.locale,
    user_agent: i.userAgent.slice(0, 300),
  };
  if (i.extra?.handover_id) ctx.handover_id = i.extra.handover_id;
  if (i.extra?.case_number) ctx.case_number = i.extra.case_number;
  return ctx;
}

/** Request body. The handover goes as top-level `handover_id` (links the item to the handover on the sponsor side). */
export function buildQualityItem(
  kind: 'problem' | 'idea',
  title: string,
  body: string,
  context: QualityContext | null,
  handoverId?: string,
): QualityItemIn {
  const out: QualityItemIn = { kind, title: title.trim().slice(0, 300) };
  const b = body.trim();
  if (b) out.body = b;
  const hid = handoverId ?? context?.handover_id;
  if (hid) out.handover_id = hid;
  if (context) {
    const { handover_id: _h, ...rest } = context;
    out.context = rest;
  }
  return out;
}
